import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { carePatientsKeys } from '@/entities/care-patients';
import { rxKeys } from '@/entities/rx';

import { bumpDemoResetEpoch } from './demoResetEpoch.js';
import { clearDemoCopyResetting } from './demoResetInFlight.js';
import { useDemoCopyResetState } from './useDemoCopyResetState.js';

/**
 * Runs the one-shot side effects of a reset leaving `resetting`. Each branch
 * ends by clearing the in-flight slug, which turns the derived state `idle` on
 * the next render, so the effect cannot see the same `settled` or `failed`
 * state twice. Mounted once, by `PatientWorkspacePage`, which outlives the
 * reset control: opening the fax editor unmounts `PatientPanel` while a reset
 * can still be in flight. The control and the overlay only read state.
 */
export function useDemoCopyResetTransitions(patientUserId: string): void {
  const queryClient = useQueryClient();
  const resetState = useDemoCopyResetState(patientUserId);

  useEffect(() => {
    if (resetState.type === 'failed') {
      toast.error(`Could not reset ${resetState.displayName}.`, {
        description: resetState.errorSummary,
      });
      clearDemoCopyResetting(patientUserId);
      return;
    }
    if (resetState.type !== 'settled') {
      return;
    }
    // The copy keeps its id across a reset, so nothing expires on its own.
    // Every patient-scoped key carries that id as an element, which covers the
    // chart, labs, timeline and diagnoses without naming them one by one.
    void queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes(patientUserId),
    });
    void queryClient.invalidateQueries({ queryKey: carePatientsKeys.all });
    void queryClient.invalidateQueries({ queryKey: rxKeys.all });
    clearDemoCopyResetting(patientUserId);
    bumpDemoResetEpoch(patientUserId);
    toast.success(`${resetState.displayName} is back at base.`);
  }, [resetState, patientUserId, queryClient]);
}
